/**
 * Miro helpers (REF-6702).
 */

/**
 * Where a callback lands when `return_to` is missing or unsafe, and after a failed/denied connect.
 * Account → Integrations is where the connection can be retried.
 */
export const MIRO_FALLBACK_PATH = "/account?tab=integrations";

/**
 * Open-redirect guard for the `return_to` the BE hands back after the OAuth callback (and the one
 * we send it). Only an in-app relative path is accepted: it must start with a single `/` — not
 * `//host` (protocol-relative) and not `/\host` (browsers normalise `\` to `/`) — and carry no
 * scheme, whitespace or control characters.
 */
export const isSafeReturnPath = (value: unknown): value is string => {
  if (typeof value !== "string" || value.length === 0) return false;
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//") || value.startsWith("/\\")) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(value)) return false;
  if (value.includes("\\")) return false;

  return true;
};

export const safeReturnPath = (value: unknown, fallback: string = MIRO_FALLBACK_PATH): string => (isSafeReturnPath(value) ? value : fallback);

/**
 * The coded error a Miro endpoint refused with. The v3 edge may answer in the legacy
 * `{ responseData: { error_code } }` envelope or with the code at the body root, so both are read.
 */
export const getMiroErrorCode = (err: any): string | undefined => {
  const data = err?.response?.data;
  // v3 edge: `{ error: { code, message } }`; legacy envelope and root shapes are read too.
  const code = data?.error?.code ?? data?.responseData?.error_code ?? data?.error_code ?? data?.code ?? data?.detail?.error_code ?? data?.detail?.code;

  return typeof code === "string" ? code : undefined;
};

export const MIRO_ERROR = {
  NOT_CONNECTED: "miro.not_connected",
  REAUTH_REQUIRED: "miro.reauth_required",
  NOT_CONFIGURED: "miro.not_configured",
  STATE_INVALID: "miro.state_invalid",
  OAUTH_EXCHANGE_FAILED: "miro.oauth_exchange_failed",
  BOARD_NOT_FOUND: "miro.board_not_found",
  BOARD_READ_ONLY: "miro.board_read_only",
  RATE_LIMITED: "miro.rate_limited",
  UPSTREAM_ERROR: "miro.upstream_error",
} as const;

/** The connection itself is unusable — the UI should send the user back to connect/reconnect. */
export const isMiroConnectionError = (code?: string): boolean => code === MIRO_ERROR.NOT_CONNECTED || code === MIRO_ERROR.REAUTH_REQUIRED || code === MIRO_ERROR.NOT_CONFIGURED;

/** Anything the Miro API prefix puts out. Used by `HttpClient` to keep 409 a rejection. */
export const MIRO_API_PATH = "integrations/miro";

/**
 * Translation key for a coded Miro error, or `null` when the catalog has no entry for it — the caller
 * then shows its generic message. `scope` is `errors` (a refused request) or `item_errors` (one item
 * of a batch). `exists` is `i18n.exists` from `useTranslation()` — injected so this module never imports the
 * i18n bootstrap. Dynamic on purpose: the codes are the BE's vocabulary, and a missing one must degrade to
 * the generic copy rather than print a raw key (`[I18N_KEY_MUST_EXIST]`).
 */
export const miroMessageKey = (code: string | undefined, scope: "errors" | "item_errors", exists: (key: string) => boolean): string | null => {
  if (!code) return null;
  const key = `miro:${scope}.${code.replace(/^miro\./, "")}`;

  return exists(key) ? key : null;
};

/**
 * The module-permission denial (`miro` missing/disabled/expired plan) is a 402. `HttpClient` has ALREADY
 * dispatched the paywall UI by then, so callers only reset their loading state — they never add a second
 * message (`[HTTP_HANDLED_RESPONSE]`).
 */
export const isPaywallError = (err: any): boolean => err?.response?.status === 402;

const MAX_RETURN_TO_LENGTH = 512;

/**
 * The `return_to` sent to `authorize`: the current path + query when it is a safe relative path within the
 * length cap, else the bare path, else Account.
 */
export const connectReturnTo = (pathname: string, search: string): string => {
  // Stricter than `isSafeReturnPath` (shared with the callback page): the BE also refuses any `://` in the
  // value, so a page carrying `?next=https://x` would turn Connect into a 422. Kept local to this helper.
  const acceptable = (value: string) => isSafeReturnPath(value) && value.length <= MAX_RETURN_TO_LENGTH && !value.includes("://");
  const full = `${pathname}${search}`;
  if (acceptable(full)) return full;
  if (acceptable(pathname)) return pathname;

  return MIRO_FALLBACK_PATH;
};

/**
 * The url the ENLARGED view of an import tile shows: the original once the BE has resolved it
 * (`GET boards/{id}/images/{item_id}/original`), the grid's own `preview_url` until then (a short-lived Miro render).
 * The one seam between the two — the modal fetches the original and swaps it in.
 */
export const miroEnlargedUrl = (image: { preview_url: string }, original?: { url: string } | null): string => original?.url || image.preview_url;

/** Milliseconds until an original url should be refetched: a little before `expires_at`, never sooner than 1 s. `null` = no expiry known. */
export const originalRefetchDelayMs = (expiresAt: string | null | undefined, now: number = Date.now()): number | null => {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return null;

  return Math.max(1000, at - now - 5000);
};

/** Id of the "Still working…" toast a backgrounded job leaves behind, so the completion can replace it. */
export const miroBackgroundToastId = (jobId: string): string => `miro-bg-${jobId}`;

/**
 * Translation key for ONE item's failure code: the `item_errors` entry, else the `errors` entry (a job-wide code such as
 * `miro.board_busy` can also land on an item), else `null` — the caller shows its generic "Failed".
 */
export const miroItemMessageKey = (code: string | undefined, exists: (key: string) => boolean): string | null => miroMessageKey(code, "item_errors", exists) ?? miroMessageKey(code, "errors", exists);

/** Downloads an image into the browser cache; resolves `true` once it decoded, `false` if it could not be loaded. */
export const preloadImage = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

/** The 202 of a `…-jobs` start call, or `null` for a handled response (`[HTTP_HANDLED_RESPONSE]`: a resolved response is not a success). */
export const acceptedJob = <T extends { job_id: string; total: number }>(res: { _handled?: boolean; responseData?: T | null } | null | undefined): T | null =>
  res?._handled ? null : res?.responseData ?? null;
